import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { connect } from 'react-redux';
import cn from 'classnames';
import { filter } from 'lodash';
import Tree, { TreeNode } from 'rc-tree';
import Loader from '../ui/Loader';
import piction, { isEditable, updateCategoryChildren } from '../../utils/piction';
import { SESSION_EXPIRED_MESSAGE } from '../../constants/sessionStateTypes';
import { sessionExpired } from '../../actions/session';
import appConfig from '../../appConfig';
import CategoriesSmallIcon from '../Icons/CategoriesSmallDefault';
import ObjectCollectionsIcon from '../Icons/ObjectCollections';

const mapDispatchToProps = dispatch => ({
  sessionExpired: err => dispatch(sessionExpired(err)),
});

class CategoryTree extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loadingKeys: new Map({}),
      categoryList: props.categoryList,
    };

    this.onLoadData = this.onLoadData.bind(this);
    this.renderIcon = this.renderIcon.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // seems redundant, but external tools/category roles doesn't work without this bit of code.
    if (this.props.isRetrievingRoot && !nextProps.isRetrievingRoot) {
      this.setState({ categoryList: nextProps.categoryList });
    }
  }

  onLoadData(treeNode) {
    const categoryID = treeNode.props.eventKey;

    // this is a hack because there seems to be an issue with rc-tree@1.8.0 where loadData is
    // triggered twice. this condition stops it from being triggered again.
    if (!this.state.loadingKeys.has(treeNode.props.eventKey)) {
      if (!treeNode.props.isCollectionType) {
        this.setState({ loadingKeys: this.state.loadingKeys.set(categoryID, true) });

        const options = {
          CATEGORY_ID: categoryID,
          RETURN_COLOUR: 'TRUE',
          READ_ONLY_CHECK: 'TRUE',
        };

        if (appConfig.categoriesSortByCC) options.SORT_ON = 'IGNORE';

        return piction.getCategories(null, options)
          .then((res) => {
            // filter out the lightboxes from the category return
            const childCategoryList = filter(res.res.ct, data => data.t !== 'L');
            this.setState({
              categoryList:
                updateCategoryChildren(this.state.categoryList, categoryID, childCategoryList),
            });
          })
          .catch((err) => {
            if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
              this.props.sessionExpired && this.props.sessionExpired(err);
            } else {
              console.warn(err);
            }
          })
          .then(() => {
            this.setState({ loadingKeys: this.state.loadingKeys.delete(categoryID) });
          });
      }
    }

    // category to retrieve children is root level (i.e. a collection), so don't worry about it
    // since the call won't work
    return Promise.resolve();
  }

  renderIcon(treeNode) {
    const isLoading = this.state.loadingKeys.get(treeNode.eventKey);
    const classNames = cn('category-tree__icon', {
      'category-tree__icon--loading': isLoading,
      'category-tree__icon--collection': !isLoading && treeNode.isCollectionType,
      'category-tree__icon--category': !isLoading && !treeNode.isCollectionType,
    });
    const style = {color: '#212529'};

    if (treeNode.categoryColour) {
      style.color = `#${treeNode.categoryColour}`;
      // add a shadow around white categories for Category Role Manager
      if (treeNode.categoryColour === 'FFFFFF') style.color = '#000';
    }
    if (treeNode.isCollectionType) {
      return <ObjectCollectionsIcon fill='#613D65' />
    } else {
      return <CategoriesSmallIcon fill={`${style.color}`} className={classNames}/>
    }
    // return <span className={classNames} style={style} />;
  }

  renderNode(categoryData) {
    const isCollectionType = categoryData.get('t') === 'CC' || categoryData.get('t') === 'CL';
    const hasChildren = (isCollectionType && categoryData.get('ct').size > 0) ||
      (!isCollectionType && categoryData.get('ch') === 'TRUE');

    return (
      <TreeNode
        title={categoryData.get('n')}
        key={categoryData.get('i')}
        isLeaf={!hasChildren}
        icon={this.renderIcon}
        categoryColour={categoryData.get('cl')}
        isCollectionType={isCollectionType}
        disabled={this.props.disableCollectionSelect && isCollectionType}
        collectionID={categoryData.get('opk')}
        isEditable={categoryData.get('rw')}
      >
        {
          hasChildren ?
            categoryData.get('ct').map(data => this.renderNode(data)) : null
        }
      </TreeNode>
    );
  }

  renderTree() {
    return (
      <Tree
        className="category-tree"
        showLine
        selectable
        defaultExpandedKeys={
          // force expansion of root/collection level categories.
          // rc-tree should have options to do this (like autoExpandParent and/or
          // defaultExpandParent) but it doesn't seem to work.
          this.props.categoryList.map(item => item.get('i')).toJS()
        }
        loadData={this.onLoadData}
        onSelect={this.props.onSelect}
      >
        { this.state.categoryList.map(data => this.renderNode(data)) }
      </Tree>
    );
  }

  render() {
    return (
      <div className="category-tree">
        { this.props.isRetrievingRoot ? <Loader /> : this.renderTree() }
      </div>
    );
  }
}

CategoryTree.propTypes = {
  categoryList: PropTypes.oneOfType([
    PropTypes.instanceOf(List),
    PropTypes.instanceOf(Array),
  ]),
  isRetrievingRoot: PropTypes.bool,
  onSelect: PropTypes.func,
  sessionExpired: PropTypes.func,
  disableCollectionSelect: PropTypes.bool,
};

export default connect(mapDispatchToProps)(CategoryTree);

export { CategoryTree as CategoryTreeNoRedux };
